import { useEffect, useState } from "react";

const getTimeDisplay = (
  targetTime,
  mode = "countdown",
  expiredTimeText = "Expired",
  displayFormat = "dhms", // d=days, h=hours, m=minutes, s=seconds
) => {
  if (!targetTime) return "Invalid Date"; // Handle missing values

  // Parse DD-MM-YYYY HH:mm:ss format
  const parts = targetTime.split(" ");
  if (parts.length !== 2) return "Invalid Date";

  const [datePart, timePart] = parts;
  const [day, month, year] = datePart.split("-");
  const [hours = "00", minutes = "00", seconds = "00"] = timePart?.split(
    ":",
  ) || ["00", "00", "00"];

  // Validate parts exist
  if (!day || !month || !year || !hours || !minutes || !seconds) {
    return "Invalid Date";
  }

  // Create date object (month is 0-indexed in JavaScript Date)
  const targetDate = new Date(year, month - 1, day, hours, minutes, seconds);

  // Validate the date
  if (isNaN(targetDate.getTime())) return "Invalid Date";

  const now = new Date();
  const diffMs =
    mode === "countdown"
      ? targetDate.getTime() - now.getTime()
      : now.getTime() - targetDate.getTime();

  // For countdown mode, show expired text if time is up
  if (mode === "countdown" && diffMs <= 0) return expiredTimeText;

  // For countup mode, don't show negative values
  if (mode === "countup" && diffMs < 0) return "Not yet started";

  // Calculate time components
  const totalSeconds = Math.floor(Math.abs(diffMs) / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  const displayDays = totalDays;
  const displayHours = totalHours % 24;
  const displayMinutes = totalMinutes % 60;
  const displaySeconds = totalSeconds % 60;

  // Build display string based on format
  const parts_display = [];

  if (displayFormat.includes("d") && displayDays > 0) {
    parts_display.push(`${displayDays}d`);
  }

  if (displayFormat.includes("h")) {
    // Show hours even if 0 when days are shown, or when it's the first unit
    if (displayFormat.includes("d")) {
      parts_display.push(`${displayHours.toString().padStart(2, "0")}h`);
    } else {
      parts_display.push(`${totalHours}h`);
    }
  }

  if (displayFormat.includes("m")) {
    parts_display.push(`${displayMinutes.toString().padStart(2, "0")}m`);
  }

  if (displayFormat.includes("s")) {
    parts_display.push(`${displaySeconds.toString().padStart(2, "0")}s`);
  }

  return parts_display.join(" ");
};

const Timer = ({
  targetDateString,
  mode = "countdown",
  expiredText = "Expired",
  updateTimeInterval = 1000, // in milliseconds
  format = "hms", // d=days, h=hours, m=minutes, s=seconds (e.g., "dhms", "hms", "dh", "ms")
}) => {
  const [timeDisplay, setTimeDisplay] = useState("");

  useEffect(() => {
    setTimeDisplay(getTimeDisplay(targetDateString, mode, expiredText, format));

    const intervalId = setInterval(() => {
      setTimeDisplay(
        getTimeDisplay(targetDateString, mode, expiredText, format),
      );
    }, updateTimeInterval);

    return () => clearInterval(intervalId);
  }, [targetDateString, mode, expiredText, updateTimeInterval, format]); // Re-run effect if props change

  return timeDisplay;
};

export default Timer;
